<template>
    <Card :title="$t('labels.products')">
        <template #header-elements>
            <button
                type="button"
                class="btn btn-outline btn-sm alpha-teal text-teal-800"
                :disabled="importing"
                @click="$refs.ProductsImportModal.show()"
            >
                <i v-if="importing" class="icon-spinner2 spinner mr-2"></i>
                {{ $t('labels.import') }}
            </button>
            <button
                type="button"
                class="btn btn-outline btn-sm alpha-teal text-teal-800"
                :disabled="exporting"
                @click="$refs.ProductsExportModal.show()"
            >
                <i v-if="exporting" class="icon-spinner2 spinner mr-2"></i>
                {{ $t('buttons.export') }}
            </button>
            <router-link class="btn btn-outline btn-sm alpha-teal text-teal-800" :to="{ name: 'CreateProductPage' }">
                {{ $t('buttons.create') }}
            </router-link>
        </template>
        <DatatableComponent
            ref="newTable"
            :config="table.config"
            :disabled-actions="table.selectedValues.length === 0"
            checkbox-key="uuid"
            v-model:selected="table.selectedValues"
            @select:action="selectAction"
        >
            <template #cell-id="{ item }">
                 <router-link :to="{ name: 'EditProductPage', params: { productUuid: item.uuid } }">
                    {{ item.id }}
                 </router-link>
            </template>
            <template #cell-main_image_url="{ item }">
                <ImageZoomComponent
                    :url="item.main_image_url !== null ? item.main_image_url : PLACEHOLDER_IMAGE_URL"
                    :alt="item.name"
                    :default-width="50"
                    :default-height="50"
                />
            </template>
            <template #cell-name="{ item }">
                <span class="long-string" :title="item.name">{{ item.name }}</span>
            </template>
            <template #cell-automatic_arrival="{ item }">
                <div v-if="item.automatic_arrival === 1">
                    <i class="icon-magic-wand mr-2 text-purple automatic-arrival-icon"></i>
                    <span>{{ $t('table.data.method_arrival.automatically') }}</span>
                </div>
                <div v-else>
                    <i class="icon-hammer mr-2 text-orange automatic-arrival-icon"></i>
                    <span>{{ $t('table.data.method_arrival.manual') }}</span>
                </div>
            </template>
            <template #cell-cost="{ item }">
                <span>{{ Price.factory(item.cost).toCurrencyString(currenciesCodeByUuid[item.cost_currency_uuid] ?? '') }}</span>
            </template>
            <template #cell-balance_with_reserve="{ item }">
                <span>
                    {{ Amount.factory(item.balance_with_reserve).toMeasurementString(item.measurement_name) }}
                </span>
            </template>
            <template #cell-reserve="{ item }">
                <span>
                    {{ Amount.factory(item.reserve).toMeasurementString(item.measurement_name) }}
                </span>
            </template>
            <template #cell-waiting="{ item }">
                <span>
                    {{ Amount.factory(item.waiting).toMeasurementString(item.measurement_name) }}
                </span>
            </template>
            <template #cell-balance="{ item }">
                <span>
                    {{ Amount.factory(item.balance).toMeasurementString(item.measurement_name) }}
                </span>
            </template>
            <template v-for="warehouse in warehouses" v-slot:[`cell-warehouse.${warehouse.uuid}.balance`]="{ item }">
                <div class="d-flex justify-content-end">
                    {{ Amount.factory(item[`warehouse.${warehouse.uuid}.balance`]).toMeasurementString(item.measurement_name) }}
                </div>
            </template>
            <template v-for="warehouse in warehouses" v-slot:[`cell-warehouse.${warehouse.uuid}.reserve`]="{ item }">
                <div class="d-flex justify-content-end">
                    {{ Amount.factory(item[`warehouse.${warehouse.uuid}.reserve`]).toMeasurementString(item.measurement_name) }}
                </div>
            </template>
            <template v-for="warehouse in warehouses" v-slot:[`cell-warehouse.${warehouse.uuid}.waiting`]="{ item }">
                <div class="d-flex justify-content-end">
                    {{ Amount.factory(item[`warehouse.${warehouse.uuid}.waiting`]).toMeasurementString(item.measurement_name) }}
                </div>
            </template>
            <template #cell-created_at="{ item }">
                {{ DateFormatter.formatDateTime(item.created_at) }}
            </template>
            <template #cell-updated_at="{ item }">
                {{ DateFormatter.formatDateTime(item.updated_at) }}
            </template>
        </DatatableComponent>
    </Card>
    <ProductsImportModal
        ref="ProductsImportModal"
        @import:started="importing = true"
    />
    <ProductsExportModal
        ref="ProductsExportModal"
        @export:started="exporting = true"
    />
    <EditInternalProductsModal
        ref="EditInternalProductsModal"
        :selectedProductItems="table.selectedValues"
        @update:table="$refs.newTable.load(), table.selectedValues = []"
    />
    <GroupProductsModal
        ref="GroupProductsModal"
        @products:grouped="() => {$refs.newTable.load(), table.selectedValues = []}"
    />
</template>
<script>
import Card from '../../../../components/new/CardComponent.vue';
import DatatableComponent from '../../../../components/new/datatable/DatatableComponent.vue';
import ProductsImportModal from './modals/ProductsImportModal.vue';
import ProductsExportModal from './modals/ProductsExportModal.vue';
import EditInternalProductsModal from './modals/EditInternalProductsModal.vue';
import GroupProductsModal from './modals/GroupProductsModal.vue';
import ImageZoomComponent from '../../../../components/new/image/ImageZoomComponent.vue';

import pageData from './json/data.json';
import { httpClient } from '../../../../../../resources/js/services/HttpClient';
import DateFormatter from '../../../../../../resources/js/services/DateFormatter.ts';
import Amount from '../../../../../../resources/js/services/Amount.ts';
import Price from '../../../../../../resources/js/services/Price/Price.ts';
import { PLACEHOLDER_IMAGE_URL } from '../../../../constants.ts';
import { chunk } from '../../../../utils.ts';
import i18n from '../../../i18n';
 
export default {
    name: 'ProductsIndexPage',
    components: {
        Card, DatatableComponent, ProductsImportModal, ProductsExportModal, EditInternalProductsModal,
        GroupProductsModal, ImageZoomComponent
    },
    data() {
        return {
            Price,
            Amount,
            DateFormatter,
            PLACEHOLDER_IMAGE_URL,
            table: {
                config: pageData.table,
                selectedValues: []
            },
            warehouses: [],
            currencies: [],
            currenciesCodeByUuid: {},
            exporting: false,
            importing: false
        }
    },
    methods: {
        async fetchCurrencies() {
            await httpClient.get('/list/currencies', {
                limit: 100
            }).then((response) => {
                this.currencies = response.data.data;
            })
        },
        formatCurrenciesCodeByUuid(currencies) {
            let result = {};

            currencies.forEach((currency) => {
                result[currency.uuid] = currency.code;
            })

            return result;
        },
        async selectAction(action) {
            if (action === 'delete') {

                await httpClient.get('/integrations/products', {
                    product_item_uuids: this.table.selectedValues,
                    limit: 0
                }).then((response) => {
                    Promise.all(chunk(response.data.data, 10).map((integrationProducts) => {
                        return httpClient.post('/batch', {
                            data: integrationProducts.map((integrationProduct) => ({
                                method: 'PATCH',
                                path: `/integrations/products/${integrationProduct.uuid}`,
                                body: {
                                    product_item_uuid: null
                                }
                            }))
                        })
                    }))
                })

                await Promise.all(chunk(this.table.selectedValues, 10).map((productItems) => {
                    return httpClient.post('/batch', {
                        data: productItems.map((productItemUuid) => ({
                            method: 'DELETE',
                            path: `/catalog/product-items/${productItemUuid}`
                        }))
                    })
                }))

                this.table.selectedValues = [];
                this.$refs.newTable.load();
            }
            if (action === 'edit') {
                this.$refs.EditInternalProductsModal.show(
                    this.$refs.newTable.getQueryParams(),
                    this.$refs.newTable.selectedAll,
                    this.$refs.newTable.total
                );
            }
            if (action === 'groupProducts') {
                this.$refs.GroupProductsModal.show(this.table.selectedValues);
            }
        },
        onExportNotification(notification) {
            if (notification.type === "App\\Notifications\\Pim\\Product\\ProductExportReadyNotification") {
                this.exporting = false;
            }
        },
        onImportNotification(notification) {
            const importDoneTypes = [
                "App\\Notifications\\Pim\\Product\\ProductImportFinishedNotification",
                "App\\Notifications\\Pim\\Product\\ProductImportFailedNotification"
            ];

            if (importDoneTypes.includes(notification.type)) {
                this.importing = false;
            }
        },
    },
    beforeMount() {
        this.table.config.templateUuid = this.$route.query.template

        for (let filter of this.table.config.filters) {
            if (!this.$route.query[filter.name]) continue

            filter.value = [].concat(this.$route.query[filter.name] ?? [])
            filter.default = 1
        }
    },
    beforeUnmount() {
        this.table.config.filters.forEach(filter => {
            filter.value = [],
            filter.default = 0
        })

        this.emitter.off('notification:received', this.onExportNotification);
        this.emitter.off('notification:received', this.onImportNotification);
    },
    async mounted() {
        await httpClient.get('/management/warehouses').then((response) => {
            this.warehouses = response.data.data;
            response.data.data.forEach(warehouse => {
                this.table.config.columns.push({
                    name: `warehouse.${warehouse.uuid}.balance`,
                    title: `${warehouse.name} (${i18n.global.t('table.titles.balance')})`,
                    type: "number",
                    default: 0,
                    orderable: 0,
                    searchable: 0,
                    additional: [],
                    required: false
                }, {
                    name: `warehouse.${warehouse.uuid}.reserve`,
                    title: `${warehouse.name} (${i18n.global.t('table.titles.reserve')})`,
                    type: "number",
                    default: 0,
                    orderable: 0,
                    searchable: 0,
                    additional: [],
                    required: false
                }, {
                    name: `warehouse.${warehouse.uuid}.waiting`,
                    title: `${warehouse.name} (${i18n.global.t('table.titles.waiting')})`,
                    type: "number",
                    default: 0,
                    orderable: 0,
                    searchable: 0,
                    additional: [],
                    required: false
                })
            })
        });
        this.table.config.filters.forEach(filter => {
            if (filter.url.includes('${projectUuid}')) {
                filter.url = filter.url.replace('${projectUuid}', this.$route.params.projectUuid);
            }
        })
        this.$refs.newTable.load();
        await this.fetchCurrencies()
        this.currenciesCodeByUuid = this.formatCurrenciesCodeByUuid(this.currencies);

        const projectResponse = await httpClient.get(`/projects/${this.$route.params.projectUuid}`);
        this.exporting = projectResponse.data.data.jobs?.product_export ?? false;
        this.importing = projectResponse.data.data.jobs?.product_import ?? false;

        this.emitter.on('notification:received', this.onExportNotification);
        this.emitter.on('notification:received', this.onImportNotification);
    },
    watch: {
        '$route.query.template'(newTemplate) {
            this.table.config.templateUuid = newTemplate ?? null;
            this.$refs.newTable.reload();
        }
    },
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    word-break: break-word;
}
.btn:focus {
    box-shadow: none !important;
}
.automatic-arrival-icon {
    font-size: 12px;
}
</style>